import {
  HOLD_TITLES,
  holdOf,
  MODULES,
  REQUIREMENT_STATUS_TITLES,
  REQUIREMENTS,
  type RequirementStatus,
  STAGES,
  whenOf,
} from '@epc/contracts';
import { Link, useRouterState } from '@tanstack/react-router';
import { useCrumbs } from '../layout/crumbs';
import { useIsDemo } from '../lib/session';
import { Badge, type Tone } from '../ui/feedback';
import { Page, PageHeader, Panel } from '../ui/layout';
import { NotFoundPage } from './NotFoundPage';

const STATUS_TONE: Record<RequirementStatus, Tone> = {
  planned: 'neutral',
  in_progress: 'info',
  partial: 'warning',
  done: 'success',
};

/** Раздел, который ещё в разработке: что в нём будет и на каком этапе. */
export function ModulePage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const mod = MODULES.find(
    (m) => m.path !== '/' && (pathname === m.path || pathname.startsWith(`${m.path}/`)),
  );
  // Подстраница раздела, который ещё в разработке, показывает сам раздел
  useCrumbs(mod ? [] : undefined);
  const demo = useIsDemo();
  if (!mod) return <NotFoundPage />;
  // В демо-компании не перечисляем функции, которых ещё нет
  if (demo) {
    return (
      <Page>
        <PageHeader title={mod.title} subtitle={mod.description} />
        <p className="max-w-2xl text-[14px] text-ink-2">Раздел пока не подключён к демо-компании.</p>
      </Page>
    );
  }

  const stage = STAGES.find((s) => s.number === mod.stage);
  const reqs = REQUIREMENTS.filter((r) =>
    mod.code === 'norms' ? ['СМ-01', 'СМ-02', 'СМ-03', 'СМ-04'].includes(r.code) : r.module === mod.code,
  );
  const groups = [...new Set(reqs.map((r) => r.group))];

  return (
    <Page>
      <PageHeader title={mod.title} subtitle={mod.description} />
      <div className="visor mb-6 flex flex-col gap-1 rounded-lg border border-line bg-surface px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
        <span className="text-[14px] font-semibold text-ink">
          Раздел появится на этапе {mod.stage}
          {stage ? `: ${stage.title.toLowerCase()}` : ''}
        </span>
        <span className="text-[14px] text-ink-2">
          {stage?.summary}
          {stage?.needs ? ` Заранее нужно: ${stage.needs}.` : ''}
        </span>
        <Link
          to="/roadmap"
          className="text-[14px] font-medium whitespace-nowrap text-accent-text underline-offset-4 hover:underline sm:ml-auto"
        >
          Ход разработки
        </Link>
      </div>

      {reqs.length ? (
        <div className="flex flex-col gap-6">
          {groups.map((g) => (
            <Panel
              key={g}
              title={g}
              meta={mod.tzSection ? `раздел ${mod.tzSection} ТЗ` : undefined}
              bodyClassName="p-0"
            >
              <ul>
                {reqs
                  .filter((r) => r.group === g)
                  .map((r) => {
                    const h = holdOf(r);
                    // Своё объяснение показываем; «ждёт своего этапа» и «2 очередь» видны и так
                    const hold = h && (r.hold || (h.kind !== 'stage' && h.kind !== 'queue')) ? h : null;
                    return (
                      <li
                        key={r.code}
                        className="flex flex-col gap-2 border-b border-line px-5 py-3 last:border-0 sm:flex-row sm:items-start sm:gap-4"
                      >
                        <span className="num w-14 shrink-0 text-[14px] font-semibold text-ink">{r.code}</span>
                        <span className="flex flex-1 flex-col gap-1">
                          <span className="text-[14px] text-ink">{r.text}</span>
                          <span className="text-[12px] text-ink-2">
                            {whenOf(r)}
                            {hold ? ` · ${HOLD_TITLES[hold.kind]}: ${hold.text}` : ''}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="text-[12px] text-ink-2">{r.priority} очередь</span>
                          <Badge tone={STATUS_TONE[r.status]}>{REQUIREMENT_STATUS_TITLES[r.status]}</Badge>
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </Panel>
          ))}
        </div>
      ) : (
        <p className="max-w-2xl text-[14px] text-ink-2">
          Требования к разделу описываются в ТЗ (разделы 6.12–6.16) и будут добавлены сюда после согласования.
        </p>
      )}
    </Page>
  );
}
